import { FunctionComponent, ReactElement, ReactNode, useMemo } from "react";
import {
  MacrosIcon,
  MetricsIcon,
  SnapshotIcon,
  AnalysisIcon,
  ExposureIcon,
  SourceIcon,
  SeedIcon,
  ModelIcon,
  GeospatialIcon,
  DatetimeIcon,
  ObjectIcon,
  NumberIcon,
  BooleanIcon,
  TextIcon,
  CteIcon,
  DbtNodeExposureIcon,
  MetricsDarkIcon,
  MetricsLightIcon,
  DbtNodeModelIcon,
  DbtNodeSeedIcon,
  DbtNodeSnapshotIcon,
  DbtNodeSourceIcon,
  DbtNodeSubqueryLightIcon,
  DbtNodeSubqueryDarkIcon,
  FunctionIcon,
  DbtNodeFunctionIcon,
} from "../assets/icons";
import styles from "./styles.module.scss";
import { Tooltip } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { useLineageContext } from "../context/LineageProvider";
import { DIALECT_ICON } from "./CustomNodes";
import { IconHeartRateMonitor } from "@tabler/icons-react";

export const ColumnDatatype: FunctionComponent<{
  datatype: string;
  color?: string;
  size?: string;
}> = ({ datatype, color, size = "1rem" }) => {
  const [Icon, compColor] = useMemo(() => {
    switch (datatype.toLowerCase()) {
      case "integer":
      case "float":
      case "double precision":
      case "double":
      case "bigint":
        return [NumberIcon, "#FF754C"];
      case "bool":
      case "boolean":
        return [BooleanIcon, "#00A5DB"];
      case "text":
      case "character":
      case "character varying":
      case "varchar":
        return [TextIcon, "#3F8CFF"];
      case "geospatial":
        return [GeospatialIcon, "#01CD8C"];
      case "date":
      case "timestamp":
      case "timestamp with time zone":
        return [DatetimeIcon, "#247EFE"];
      default:
        return [ObjectIcon, "#6A24FE"];
    }
  }, [datatype]);

  return (
    <Stack style={{ color: color || compColor }} align="center">
      <Icon width={size} height={size} />
    </Stack>
  );
};

const NodeTypeIconsWithColors: Record<string, { light: ReactNode; dark: ReactNode }> = {
  seed: {
    light: <DbtNodeSeedIcon style={{ color: "#E6FAF4" }} />,
    dark: <DbtNodeSeedIcon style={{ color: "#344B49" }} />,
  },
  model: {
    light: <DbtNodeModelIcon style={{ color: "#FFECE6" }} />,
    dark: <DbtNodeModelIcon style={{ color: "#4D4343" }} />,
  },
  cte: {
    light: <CteIcon style={{ color: "#FDF6EA" }} />,
    dark: <CteIcon style={{ color: "#4B473F" }} />,
  },
  subquery: {
    light: <DbtNodeSubqueryLightIcon style={{ color: "#FDF6EA" }} />,
    dark: <DbtNodeSubqueryDarkIcon style={{ color: "#4B473F" }} />,
  },
  source: {
    light: <DbtNodeSourceIcon style={{ color: "#EAF3FF" }} />,
    dark: <DbtNodeSourceIcon style={{ color: "#384454" }} />,
  },
  exposure: {
    light: <DbtNodeExposureIcon style={{ color: "#FEEFF7" }} />,
    dark: <DbtNodeExposureIcon style={{ color: "#4C404C" }} />,
  },
  analysis: { light: <AnalysisIcon />, dark: <AnalysisIcon /> },
  snapshot: {
    light: <DbtNodeSnapshotIcon style={{ color: "#F6F4FF" }} />,
    dark: <DbtNodeSnapshotIcon style={{ color: "#444554" }} />,
  },
  semantic_model: { light: <MetricsLightIcon />, dark: <MetricsDarkIcon /> },
  macros: { light: <MacrosIcon />, dark: <MacrosIcon /> },
  function: {
    light: <DbtNodeFunctionIcon style={{ color: "#F0EAFF" }} />,
    dark: <DbtNodeFunctionIcon style={{ color: "#3D3654" }} />,
  },
  unknown: {
    light: <DbtNodeModelIcon style={{ color: "#FFECE6" }} />,
    dark: <DbtNodeModelIcon style={{ color: "#4D4343" }} />,
  },
};

export const NodeTypeIcon: FunctionComponent<{ nodeType: string }> = ({ nodeType }) => (
  <div>
    {nodeType === "seed" && <SeedIcon />}
    {nodeType === "model" && <ModelIcon />}
    {nodeType === "cte" && <ModelIcon />}
    {nodeType === "subquery" && <ModelIcon />}
    {nodeType === "source" && <SourceIcon />}
    {nodeType === "exposure" && <ExposureIcon />}
    {nodeType === "analysis" && <AnalysisIcon />}
    {nodeType === "snapshot" && <SnapshotIcon />}
    {nodeType === "semantic_model" && <MetricsIcon />}
    {nodeType === "macros" && <MacrosIcon />}
    {nodeType === "unknown" && <ModelIcon />}
    {nodeType === "function" && <FunctionIcon />}
    {nodeType === "dashboard" && <IconHeartRateMonitor />}
  </div>
);

export const NodeIcon = ({ nodeType }: { nodeType: string }) => {
  return nodeType in NODE_TYPE_SHORTHAND ? (
    <NodeTypeIcon nodeType={nodeType} />
  ) : nodeType in DIALECT_ICON ? (
    <img
      src={DIALECT_ICON[nodeType as keyof typeof DIALECT_ICON]}
      className={styles.dialect_icon}
      alt={`${nodeType} dialect icon`}
    />
  ) : (
    <div />
  );
};

export const NodeTypeIconForSQL: FunctionComponent<{
  nodeType: string;
}> = ({ nodeType }) => {
  const {
    state: { theme },
  } = useLineageContext();
  return <>{NodeTypeIconsWithColors[nodeType][theme]}</>;
};

export const TableNodePill: FunctionComponent<{
  id: string;
  icon: ReactElement;
  label: string;
  text: string;
}> = ({ id, icon, text, label }) => (
  <Tooltip title={label}>
    <div className={styles.table_node_pill} id={id}>
      <div className={styles.icon}>{icon}</div>
      <div>{text}</div>
    </div>
  </Tooltip>
);

export const NODE_TYPE_STYLES = {
  seed: styles.seed,
  model: styles.model,
  source: styles.source,
  exposure: styles.exposure,
  snapshot: styles.snapshot,
  semantic_model: styles.metrics,
  macros: styles.macros,
  analysis: styles.analysis,
  cte: styles.model,
  subquery: styles.model,
  unknown: styles.exposure,
  function: styles.function_node,
  dashboard: styles.dashboard,
};

export const NODE_TYPE_SHORTHAND = {
  seed: "SED",
  model: "MDL",
  source: "SRC",
  exposure: "EXP",
  snapshot: "SNP",
  semantic_model: "MET",
  macros: "SEM",
  analysis: "ANY",
  cte: "CTE",
  subquery: "SUB",
  function: "FNC",
  dashboard: "BI",
  unknown: "UNK",
};
